import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { TrendingUp, TrendingDown, ArrowRight, Home } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Button } from "@/components/ui/button";
import { useBrand, useTenantPath } from "@/lib/brand";
import { api } from "@/lib/api";
import { buildBreadcrumbJsonLd } from "@/lib/seo";

/**
 * One area's monthly house-price report — /area/:area/house-prices.
 *
 * The same snapshot prerender.py serves to crawlers, so the two never
 * disagree: both read the cached report written by
 * scripts/build_area_reports.py rather than rebuilding it per request.
 */
function toTitleCase(slug) {
  return (slug || "")
    .split("-")
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");
}

const gbp = (n) =>
  typeof n === "number" && n > 0
    ? `£${n.toLocaleString("en-GB", { maximumFractionDigits: 0 })}`
    : "-";

export default function AreaPricesPage() {
  const brand = useBrand();
  const tenantPath = useTenantPath();
  const { slug: urlSlug, area: areaSlug } = useParams();
  const biz = brand.business || {};
  const tenantSlug = urlSlug || brand.tenant_slug;
  const areaName = toTitleCase(areaSlug);

  const [report, setReport] = useState(null);
  const [state, setState] = useState("loading");

  const load = useCallback(async () => {
    if (!tenantSlug || !areaSlug) return;
    setState("loading");
    try {
      const { data } = await api.get(`/areas/${areaSlug}/prices`, {
        params: { tenant: tenantSlug },
      });
      setReport(data);
      setState("ready");
    } catch {
      setState("empty");
    }
  }, [tenantSlug, areaSlug]);

  useEffect(() => { load(); }, [load]);

  const scopeName = report?.data_scope_name || areaName;
  // Only call it a widening when the district really is a different place —
  // Woking's district is also called Woking, and saying "these figures
  // actually cover Woking" on the Woking page reads as an error.
  const widened =
    report?.data_scope === "district" &&
    scopeName.toLowerCase() !== areaName.toLowerCase();

  const pageTitle = report
    ? `${areaName} House Prices ${report.month_label}, Sold Prices & Trends`
    : `${areaName} House Prices, ${biz.name || ""}`;

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={pageTitle}
        description={
          report?.summary_line
            ? `${report.summary_line} Updated monthly by ${biz.name || "us"}.`
            : `HM Land Registry sold prices for ${areaName}, updated monthly.`
        }
        jsonLd={buildBreadcrumbJsonLd([
          { name: "Home", url: `${tenantPath}` },
          { name: areaName, url: `${tenantPath}/area/${areaSlug}` },
          { name: "House prices", url: `${tenantPath}/area/${areaSlug}/house-prices` },
        ])}
      />
      <SiteHeader />

      <main className="max-w-5xl mx-auto px-6 py-16">
        <nav className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
          <Link to={`${tenantPath}/area/${areaSlug}`} className="hover:text-primary">
            {areaName}
          </Link>
          <span className="mx-2">/</span>
          <span>House prices</span>
        </nav>

        <h1 className="font-serif text-4xl md:text-5xl text-primary mb-4">
          {areaName} house prices
          {report ? <span className="block text-2xl md:text-3xl opacity-70 mt-2">{report.month_label}</span> : null}
        </h1>

        {state === "loading" && (
          <p className="text-muted-foreground">Loading the latest sold-price data…</p>
        )}

        {state === "empty" && (
          <div className="border border-border rounded-sm p-8 bg-card">
            <p className="text-primary/85 leading-relaxed">
              We haven't published a price report for {areaName} yet. For a figure
              specific to your own address, run a free instant valuation, it uses
              your property's own Land Registry history.
            </p>
            <Button asChild className="mt-6">
              <Link to={`${tenantPath}/valuation`}>
                Get a free valuation <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}

        {state === "ready" && report && (
          <>
            <p className="text-lg text-primary/85 leading-relaxed max-w-3xl">
              {report.summary_line}
            </p>

            {widened && (
              <p className="mt-6 text-sm text-muted-foreground leading-relaxed max-w-3xl border-l-2 border-accent pl-4">
                HM Land Registry records sales against a postal town, and {areaName} is
                not one: its post is handled through a neighbouring town. These figures
                therefore cover <strong>{scopeName}</strong>, the wider local authority
                {" "}{areaName} sits in. It is the closest honest measure available; for a
                figure specific to your street, ask us.
              </p>
            )}

            {report.kpis?.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-border mt-10 border border-border">
                {report.kpis.map((kpi, i) => (
                  <div key={i} className="bg-card p-6">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground">
                      {kpi.label}
                    </div>
                    <div className="font-serif text-3xl text-primary mt-2">{kpi.value}</div>
                    {kpi.delta && (
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        {kpi.delta.startsWith("▲") && <TrendingUp className="h-3 w-3" />}
                        {kpi.delta.startsWith("▼") && <TrendingDown className="h-3 w-3" />}
                        {kpi.delta.replace(/^[▲▼]\s*/, "")}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {report.commentary?.paragraphs?.length > 0 && (
              <section className="mt-14">
                <h2 className="font-serif text-2xl text-primary mb-4">What the numbers say</h2>
                <div className="space-y-4 text-primary/85 leading-relaxed max-w-3xl">
                  {report.commentary.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
                </div>
                {report.commentary.insights?.length > 0 && (
                  <ul className="mt-8 space-y-3 max-w-3xl">
                    {report.commentary.insights.map((ins, i) => (
                      <li key={i} className="flex gap-3 text-primary/85">
                        <span className="text-accent-ink font-serif text-lg leading-none">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span>{ins}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {report.property_types?.length > 0 && (
              <section className="mt-14">
                <h2 className="font-serif text-2xl text-primary mb-4">What sold, and for how much</h2>
                <div className="overflow-x-auto border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-card border-b border-border">
                      <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                        <th className="p-4">Property type</th>
                        <th className="p-4">Sales</th>
                        <th className="p-4">Share</th>
                        <th className="p-4">Average price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.property_types.map((t, i) => (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="p-4 text-primary">{t.type}</td>
                          <td className="p-4">{t.count}</td>
                          <td className="p-4">{t.pct}%</td>
                          <td className="p-4 font-medium text-primary">{gbp(t.avg_price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {report.price_history?.length > 1 && (
              <section className="mt-14">
                <h2 className="font-serif text-2xl text-primary mb-2">
                  {scopeName} prices over {report.price_history.length} years
                </h2>
                <p className="text-sm text-muted-foreground mb-4 max-w-3xl">
                  Each row averages one twelve-month period, trimmed to the 10th–90th
                  percentile so a single unusual sale cannot move it.
                </p>
                <div className="overflow-x-auto border border-border">
                  <table className="w-full text-sm">
                    <thead className="bg-card border-b border-border">
                      <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
                        <th className="p-4">12 months to</th>
                        <th className="p-4">Average price</th>
                        <th className="p-4">Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {report.price_history.map((h, i) => (
                        <tr key={i} className="border-b border-border last:border-0">
                          <td className="p-4 text-primary">{h.label}</td>
                          <td className="p-4 font-medium text-primary">{gbp(h.avg_price)}</td>
                          <td className="p-4">{h.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            <section className="mt-14 border border-border bg-card p-8">
              <h2 className="font-serif text-2xl text-primary mb-3">
                What your own home is worth
              </h2>
              <p className="text-primary/85 leading-relaxed max-w-2xl">
                An area average is a starting point, not a valuation. The same street
                can span twice these figures depending on size, condition and aspect.
              </p>
              <div className="flex flex-wrap gap-3 mt-6">
                <Button asChild>
                  <Link to={`${tenantPath}/valuation`}>
                    Get a free instant valuation <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline">
                  <Link to={`${tenantPath}/area/${areaSlug}`}>
                    <Home className="mr-2 h-4 w-4" /> Homes in {areaName}
                  </Link>
                </Button>
              </div>
            </section>

            {report.methodology && (
              <p className="mt-10 text-xs text-muted-foreground max-w-3xl leading-relaxed">
                {report.methodology} Contains HM Land Registry data © Crown copyright and
                database right {new Date().getFullYear()}, licensed under the Open
                Government Licence v3.0. Updated monthly.
              </p>
            )}
          </>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
